import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import type { BoardView, Sprint } from '../types';

/**
 * Доска с перетаскиванием карточек. Позиция задаётся соседями (перед какой карточкой бросили),
 * а не индексом: два человека, двигающие карточки одновременно, не сойдутся в индексах, но
 * сойдутся в «между этими двумя».
 */
export function BoardPage() {
  const { tenantId = '', boardId = '' } = useParams();
  const queryClient = useQueryClient();
  const [sprintId, setSprintId] = useState('');
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const view = useQuery({
    queryKey: ['board-view', boardId, sprintId],
    queryFn: () =>
      api.get<BoardView>(
        `/tenants/${tenantId}/boards/${boardId}/view${sprintId ? `?sprintId=${sprintId}` : ''}`,
      ),
  });

  const sprints = useQuery({
    queryKey: ['sprints', boardId],
    queryFn: () => api.get<Sprint[]>(`/tenants/${tenantId}/boards/${boardId}/sprints`),
  });

  const move = useMutation({
    mutationFn: (args: { issueId: string; statusId: string; beforeIssueId?: string }) =>
      api.patch(`/tenants/${tenantId}/issues/${args.issueId}/rank`, {
        statusId: args.statusId,
        beforeIssueId: args.beforeIssueId,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['board-view', boardId] }),
  });

  if (view.isLoading) return <div className="state">Загрузка…</div>;
  if (view.isError) return <div className="state">{(view.error as ApiError).message}</div>;

  const board = view.data!;

  return (
    <>
      <div className="page-head">
        <div>
          <Link className="small" to={`/t/${tenantId}/boards`}>
            ← Доски
          </Link>
          <h1>{board.name}</h1>
        </div>
        <div className="spacer" />
        {sprints.data && sprints.data.length > 0 && (
          <select
            className="input"
            style={{ width: 'auto' }}
            value={sprintId}
            onChange={(event) => setSprintId(event.target.value)}
          >
            <option value="">Все задачи</option>
            {sprints.data.map((sprint) => (
              <option key={sprint.id} value={sprint.id}>
                {sprint.name} · {SPRINT_LABELS[sprint.status]} ({sprint.issueCount})
              </option>
            ))}
          </select>
        )}
      </div>

      {move.isError && <div className="form-error">{(move.error as ApiError).message}</div>}

      <div className="board">
        {board.columns.map((column) => {
          const statusId = column.statusIds[0];
          return (
            <div
              key={column.columnId}
              className={[
                'board-column',
                column.wipExceeded ? 'over-wip' : '',
                dropTarget === column.columnId ? 'drop-target' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onDragOver={(event) => {
                event.preventDefault();
                setDropTarget(column.columnId);
              }}
              onDragLeave={() => setDropTarget((current) => (current === column.columnId ? null : current))}
              onDrop={() => {
                setDropTarget(null);
                // Колонка может собирать несколько статусов; при переносе кладём в первый из них.
                if (dragging && statusId) move.mutate({ issueId: dragging, statusId });
                setDragging(null);
              }}
            >
              <div className="board-column-head">
                {column.name}
                <span className="board-column-count">
                  {column.total}
                  {column.wipLimit ? ` / ${column.wipLimit}` : ''}
                </span>
              </div>

              {column.issues.map((issue) => (
                <div
                  key={issue.id}
                  className={`board-card ${dragging === issue.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => setDragging(issue.id)}
                  onDragEnd={() => setDragging(null)}
                  onDrop={(event) => {
                    // Бросок на карточку = встать перед ней.
                    event.stopPropagation();
                    setDropTarget(null);
                    if (dragging && dragging !== issue.id && statusId) {
                      move.mutate({ issueId: dragging, statusId, beforeIssueId: issue.id });
                    }
                    setDragging(null);
                  }}
                >
                  <Link className="board-card-key" to={`/t/${tenantId}/issues/${issue.key}`}>
                    {issue.key}
                  </Link>
                  <div className="board-card-summary">{issue.summary}</div>
                  {issue.counters.checklistTotal > 0 && (
                    <div className="muted small" style={{ marginTop: 4 }}>
                      Чеклист: {issue.counters.checklistDone}/{issue.counters.checklistTotal}
                    </div>
                  )}
                </div>
              ))}

              {column.issues.length === 0 && <div className="muted small">пусто</div>}
            </div>
          );
        })}
      </div>
    </>
  );
}

const SPRINT_LABELS: Record<Sprint['status'], string> = {
  PLANNED: 'запланирован',
  ACTIVE: 'идёт',
  COMPLETED: 'завершён',
};